import { getApiUrl } from './apiConfig';

export async function fetchStudentNoteById(noteId, { viewerStudentId } = {}) {
  const q =
    viewerStudentId != null
      ? `?viewer_student_id=${encodeURIComponent(String(viewerStudentId))}`
      : '';
  const res = await fetch(`${getApiUrl()}/api/student-notes/${encodeURIComponent(String(noteId))}${q}`);
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load note');
  }
  return res.json();
}

export async function fetchStudentNotesForUser(userId, { limit = 100 } = {}) {
  const res = await fetch(
    `${getApiUrl()}/api/student-notes?createdBy=${encodeURIComponent(userId)}&limit=${limit}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load notes');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** Own notes ordered by last modified (server sorts by updated_at). */
export async function fetchMyNotes(userId, { limit = 100 } = {}) {
  return fetchStudentNotesForUser(userId, { limit });
}

/** Own notes marked favourite (isFavourite on author's record). */
export async function fetchFavouriteNotes(userId, { limit = 100 } = {}) {
  const res = await fetch(
    `${getApiUrl()}/api/student-notes?favouritesFor=${encodeURIComponent(userId)}&limit=${limit}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load favourite notes');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

/** Notes where the student id appears in favouritedBy (e.g. starred shared notes). */
export async function fetchOtherFavouriteNotes(userId, { limit = 100 } = {}) {
  const res = await fetch(
    `${getApiUrl()}/api/student-notes?otherFavouritesFor=${encodeURIComponent(userId)}&limit=${limit}`
  );
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load other favourite notes');
  }
  const data = await res.json();
  return Array.isArray(data) ? data : [];
}

export async function createStudentNote({
  createdBy,
  title,
  body,
  images = [],
  readPermission,
  commentPermission,
  editPermission,
}) {
  const payload = { createdBy, title, body, images };
  if (readPermission !== undefined) payload.readPermission = readPermission;
  if (commentPermission !== undefined) payload.commentPermission = commentPermission;
  if (editPermission !== undefined) payload.editPermission = editPermission;
  const res = await fetch(`${getApiUrl()}/api/student-notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to save note');
  }
  return res.json();
}

export async function updateStudentNote(id, payload) {
  const res = await fetch(`${getApiUrl()}/api/student-notes/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to update note');
  }
  return res.json();
}

export async function sendStudentNotification({ userId, text, link, createdBy }) {
  const res = await fetch(`${getApiUrl()}/api/notifications`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      text,
      link: link ?? null,
      created_by: createdBy,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to send notification');
  }
  return res.json();
}

export async function deleteStudentNote(id) {
  const res = await fetch(`${getApiUrl()}/api/student-notes/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to delete note');
  }
  return res.json();
}
